import { FC, ReactNode, useState } from 'react';
import { Box, FormLabel, IconButton, InputAdornment, OutlinedInput, Typography } from '@mui/material';

import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

import { useIsMobile } from '../../hooks/useWindowWidth';

import styles from './CustomPassInput.module.scss';

export type CustomPassInputProps = {
  customClass?: string;
  error?: string;
  name?: string;
  label: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void | Promise<void>;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  maskDisplayChar?: string;
  visibilityToggleIcons?: {
    concealed: ReactNode;
    revealed: ReactNode;
  };
};

const CustomPassInput: FC<CustomPassInputProps> = ({
  customClass,
  error = '',
  name = 'password',
  label,
  value,
  onChange,
  onBlur,
  maskDisplayChar = '*',
  visibilityToggleIcons,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const isMobile = useIsMobile();
  const isTouchDevice =
    typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);

  const useCustomMask = Boolean(maskDisplayChar?.length) && !isMobile && !isTouchDevice;
  const maskChar = maskDisplayChar?.[0] ?? '*';

  const handleClickShowPassword = () => setShowPassword(show => !show);

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleMouseUpPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const emitValue = (next: string) => {
    onChange?.({
      target: { name, value: next },
    } as React.ChangeEvent<HTMLInputElement>);
  };

  const handleMaskedKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!useCustomMask || showPassword) return;
    const current = value ?? '';
    if (e.key === 'Backspace') {
      e.preventDefault();
      emitValue(current.slice(0, -1));
      return;
    }
    if (e.key === 'Delete') {
      e.preventDefault();
      emitValue(current.slice(0, -1));
      return;
    }
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'Tab' || e.key === 'Enter') return;
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.key.length === 1) {
      e.preventDefault();
      emitValue(current + e.key);
    }
  };

  const handleMaskedPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (!useCustomMask || showPassword) return;
    e.preventDefault();
    const pasted = e.clipboardData.getData('text');
    const current = value ?? '';
    emitValue(current + pasted);
  };

  const handleMaskedFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (useCustomMask && !showPassword) {
      const input = e.currentTarget;
      const len = (value ?? '').length;
      queueMicrotask(() => {
        try {
          input.setSelectionRange(len, len);
        } catch {
          /* ignore */
        }
      });
    }
  };

  const concealedIcon = visibilityToggleIcons?.concealed ?? <VisibilityOffOutlinedIcon />;
  const revealedIcon = visibilityToggleIcons?.revealed ?? <VisibilityOutlinedIcon />;

  const inputType = useCustomMask ? 'text' : showPassword ? 'text' : 'password';

  const displayValue = useCustomMask && !showPassword ? maskChar.repeat((value ?? '').length) : (value ?? '');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (useCustomMask && !showPassword) return;
    onChange?.(e);
  };

  return (
    <Box
      id={styles.customInputContainer}
      className={customClass}
    >
      <FormLabel className={styles.customInputLabel}>
        <Typography variant="h11Semibold">{label}</Typography>
      </FormLabel>
      <OutlinedInput
        type={inputType}
        name={name}
        required
        {...(value !== undefined ? { value: displayValue } : {})}
        className={`${error && styles.error} ${styles.customInput}`}
        sx={
          useCustomMask && !showPassword
            ? {
                height: 44,
                alignItems: 'center',
                '& .MuiOutlinedInput-input': {
                  boxSizing: 'border-box',
                  height: 44,
                  paddingTop: 0,
                  paddingBottom: 0,
                  paddingLeft: '20px',
                  lineHeight: '44px',
                },
              }
            : undefined
        }
        placeholder="Password"
        onChange={handleChange}
        onBlur={onBlur}
        inputProps={{
          autoComplete: 'current-password',
          ...(useCustomMask && !showPassword
            ? {
                onKeyDown: handleMaskedKeyDown,
                onPaste: handleMaskedPaste,
                onFocus: handleMaskedFocus,
              }
            : {}),
        }}
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              aria-label={showPassword ? 'hide the password' : 'display the password'}
              onClick={handleClickShowPassword}
              onMouseDown={handleMouseDownPassword}
              onMouseUp={handleMouseUpPassword}
              edge="end"
              className={styles.visibilityToggleIcon}
            >
              {!showPassword ? concealedIcon : revealedIcon}
            </IconButton>
          </InputAdornment>
        }
        label={label}
      />
      {error && (
        <Typography
          variant="h11"
          className={styles.errorMessage}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default CustomPassInput;
